import { SettlementData, formatPaymentBreakdown } from '@/hooks/useRegisterSettlement';
import { printHtml } from '@/utils/print';

interface SettlementPrintViewProps {
  data: SettlementData;
  settledByName: string;
  formatCurrency: (amount: number) => string;
}

// Generate payment breakdown HTML section
// This dynamically generates sections for all payment methods in the data
function generatePaymentBreakdownHtml(
  data: SettlementData,
  formatCurrency: (amount: number) => string
): string {
  // Check if there's any payment breakdown data
  if (!data.payment_breakdown || Object.keys(data.payment_breakdown).length === 0) {
    return '';
  }

  const breakdown = formatPaymentBreakdown(data.payment_breakdown);

  // Calculate totals
  const totalPayments = breakdown.reduce((sum, pm) => sum + pm.total, 0);
  const totalCount = breakdown.reduce((sum, pm) => sum + pm.count, 0);

  let html = `
    <div class="section">
      <div class="section-title">Payment Breakdown</div>
      <div class="row" style="font-size: 10px; color: #666; margin-bottom: 8px;">
        <span>Method / Type</span>
        <span>Count &nbsp; Amount</span>
      </div>
  `;

  for (const method of breakdown) {
    // Use the formatted methodLabel from formatPaymentBreakdown (handles new payment methods dynamically)
    html += `
      <div class="payment-method-header">
        <div class="row">
          <span>${method.methodLabel}</span>
          <span>${method.count} &nbsp; ${formatCurrency(method.total)}</span>
        </div>
      </div>
    `;

    // Payment type breakdown rows (if any)
    if (method.types && method.types.length > 0) {
      for (const type of method.types) {
        html += `
          <div class="payment-type-row">
            <div class="row">
              <span>- ${type.type_name}</span>
              <span>${type.count} &nbsp; ${formatCurrency(type.total)}</span>
            </div>
          </div>
        `;
      }
    }
  }

  // Total row
  html += `
      <div class="row total">
        <span>TOTAL PAYMENTS:</span>
        <span>${totalCount} &nbsp; ${formatCurrency(totalPayments)}</span>
      </div>
    </div>
  `;

  return html;
}

const SETTLEMENT_STYLES = `
  body {
    font-family: 'Courier New', monospace !important;
    font-size: 12px;
    line-height: 1.4;
    max-width: 350px;
    margin: 0 auto;
  }
  .header {
    text-align: center;
    border-bottom: 2px dashed #000;
    padding-bottom: 15px;
    margin-bottom: 15px;
  }
  .header h1 {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  .header h2 {
    font-size: 14px;
    font-weight: normal;
  }
  .section {
    margin-bottom: 15px;
    border-bottom: 1px dashed #ccc;
    padding-bottom: 10px;
  }
  .section-title {
    font-weight: bold;
    text-align: center;
    margin-bottom: 8px;
    font-size: 11px;
    text-transform: uppercase;
  }
  .row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 3px;
  }
  .row.indent {
    padding-left: 15px;
  }
  .row.total {
    font-weight: bold;
    border-top: 1px solid #000;
    margin-top: 5px;
    padding-top: 5px;
  }
  .row.subtraction .label::before {
    content: '(-)';
    margin-right: 5px;
  }
  .row.addition .label::before {
    content: '(+)';
    margin-right: 5px;
  }
  .info-block {
    margin-bottom: 10px;
  }
  .info-row {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
  }
  .variance {
    text-align: center;
    padding: 10px;
    font-weight: bold;
    font-size: 14px;
  }
  .variance.over {
    background-color: #d4edda;
  }
  .variance.short {
    background-color: #f8d7da;
  }
  .void-item, .refund-item {
    font-size: 10px;
    margin-bottom: 3px;
    padding-left: 10px;
  }
  .item-row {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
  }
  .item-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
  }
  .item-qty {
    width: 40px;
    text-align: center;
  }
  .item-amount {
    width: 70px;
    text-align: right;
  }
  .footer {
    text-align: center;
    margin-top: 20px;
    padding-top: 10px;
    border-top: 2px dashed #000;
    font-size: 10px;
  }
  .session-type {
    text-align: center;
    font-size: 10px;
    padding: 5px;
    margin-bottom: 10px;
    background-color: #e3f2fd;
    border-radius: 4px;
  }
  .payment-method-header {
    font-weight: bold;
    margin-top: 8px;
    padding-bottom: 3px;
    border-bottom: 1px dotted #999;
  }
  .payment-type-row {
    padding-left: 15px;
    font-size: 10px;
  }
`;

export function generateSettlementContent({
  data,
  settledByName,
  formatCurrency,
}: SettlementPrintViewProps): string {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const varianceText = data.variance > 0 ? 'OVER' : data.variance < 0 ? 'SHORT' : 'BALANCED';
  const varianceClass = data.variance > 0 ? 'over' : data.variance < 0 ? 'short' : '';

  // Generate payment breakdown section
  const paymentBreakdownHtml = generatePaymentBreakdownHtml(data, formatCurrency);

  return `
    <div class="header">
      <h1>REGISTER SETTLEMENT</h1>
      <h2>DAILY SUMMARY</h2>
    </div>

    ${data.session_type === 'virtual' ? `
    <div class="session-type">
      VIRTUAL SESSION (Admin/Manager)
    </div>
    ` : ''}

    <div class="info-block">
      <div class="info-row">
        <span>Location:</span>
        <span>${data.location_name}</span>
      </div>
      <div class="info-row">
        <span>${data.session_type === 'virtual' ? 'Session:' : 'Register:'}</span>
        <span>${data.register_name}</span>
      </div>
      <div class="info-row">
        <span>${data.session_type === 'virtual' ? 'User:' : 'Cashier:'}</span>
        <span>${data.cashier_name}</span>
      </div>
      <div class="info-row">
        <span>Date:</span>
        <span>${formatDate(data.opened_at)}</span>
      </div>
      <div class="info-row">
        <span>Opened:</span>
        <span>${formatTime(data.opened_at)}</span>
      </div>
      ${data.closed_at ? `
      <div class="info-row">
        <span>Closed:</span>
        <span>${formatTime(data.closed_at)}</span>
      </div>
      ` : ''}
    </div>

    <div class="section">
      <div class="section-title">Sales Summary</div>
      <div class="row">
        <span>Gross Sales:</span>
        <span>${formatCurrency(data.gross_sales)}</span>
      </div>
      <div class="row subtraction">
        <span class="label">Discounts:</span>
        <span>${formatCurrency(data.discount_amount)}</span>
      </div>
      <div class="row subtraction">
        <span class="label">Refunds:</span>
        <span>${formatCurrency(data.refund_amount)}</span>
      </div>
      <div class="row subtraction">
        <span class="label">Voids:</span>
        <span>${formatCurrency(data.void_amount)}</span>
      </div>
      <div class="row total">
        <span>NET SALES:</span>
        <span>${formatCurrency(data.net_sales)}</span>
      </div>
      <div class="row">
        <span>Tax Collected:</span>
        <span>${formatCurrency(data.tax_collected)}</span>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Orders</div>
      <div class="row">
        <span>Total Orders:</span>
        <span>${data.total_orders}</span>
      </div>
      <div class="row">
        <span>Completed:</span>
        <span>${data.completed_orders}</span>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Cash Drawer</div>
      <div class="row">
        <span>Opening Float:</span>
        <span>${formatCurrency(data.opening_balance)}</span>
      </div>
      <div class="row addition">
        <span class="label">Cash Sales:</span>
        <span>${formatCurrency(data.cash_payments)}</span>
      </div>
      <div class="row subtraction">
        <span class="label">Cash Refunds:</span>
        <span>${formatCurrency(data.cash_refunds)}</span>
      </div>
      <div class="row addition">
        <span class="label">Pay-Ins:</span>
        <span>${formatCurrency(data.pay_ins)}</span>
      </div>
      <div class="row subtraction">
        <span class="label">Pay-Outs:</span>
        <span>${formatCurrency(data.pay_outs)}</span>
      </div>
      <div class="row total">
        <span>EXPECTED:</span>
        <span>${formatCurrency(data.expected_balance)}</span>
      </div>
      <div class="row">
        <span>COUNTED:</span>
        <span>${formatCurrency(data.closing_balance)}</span>
      </div>
      <div class="variance ${varianceClass}">
        VARIANCE: ${formatCurrency(Math.abs(data.variance))} ${varianceText}
      </div>
    </div>

    ${paymentBreakdownHtml}

    ${data.void_count > 0 ? `
    <div class="section">
      <div class="section-title">Void Summary</div>
      <div class="row">
        <span>Total Voids:</span>
        <span>${data.void_count} (${formatCurrency(data.void_amount)})</span>
      </div>
      ${data.voids_summary.map(v => `
        <div class="void-item">
          #${v.order_number} - ${formatCurrency(v.amount)} - ${v.reason}
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${data.discount_count > 0 ? `
    <div class="section">
      <div class="section-title">Discount Summary</div>
      <div class="row">
        <span>Total Discounts:</span>
        <span>${data.discount_count} (${formatCurrency(data.discount_amount)})</span>
      </div>
    </div>
    ` : ''}

    ${data.refund_count > 0 ? `
    <div class="section">
      <div class="section-title">Refund Summary</div>
      <div class="row">
        <span>Total Refunds:</span>
        <span>${data.refund_count} (${formatCurrency(data.refund_amount)})</span>
      </div>
      ${data.refunds_summary.map(r => `
        <div class="refund-item">
          #${r.order_number} - ${formatCurrency(r.amount)} - ${r.reason}
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${data.items_sold_summary && data.items_sold_summary.length > 0 ? `
    <div class="section">
      <div class="section-title">Items Sold</div>
      <div class="item-row" style="font-weight: bold; margin-bottom: 5px;">
        <span class="item-name">Item</span>
        <span class="item-qty">Qty</span>
        <span class="item-amount">Amount</span>
      </div>
      ${data.items_sold_summary.slice(0, 20).map(item => `
        <div class="item-row">
          <span class="item-name">${item.name}</span>
          <span class="item-qty">${item.quantity}</span>
          <span class="item-amount">${formatCurrency(item.total)}</span>
        </div>
      `).join('')}
      ${data.items_sold_summary.length > 20 ? `
        <div class="item-row" style="font-style: italic;">
          <span>... and ${data.items_sold_summary.length - 20} more items</span>
        </div>
      ` : ''}
    </div>
    ` : ''}

    <div class="footer">
      <p>Settled By: ${settledByName}</p>
      <p>Settlement Time: ${new Date().toLocaleString()}</p>
      <br>
      <p>*** END OF SETTLEMENT ***</p>
    </div>
  `;
}

export function printSettlementReport(props: SettlementPrintViewProps) {
  const content = generateSettlementContent(props);
  printHtml({
    title: `Register Settlement - ${props.data.register_name}`,
    content,
    extraStyles: SETTLEMENT_STYLES,
  });
}
